import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, University } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const COUNTRIES = [
  {
    code: "GB",
    name: "United Kingdom",
    img: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format",
    gradient: "from-rose-500/40 via-indigo-900/50 to-ink",
    unis: "Oxford · Cambridge · Manchester",
    intake: "Sep / Jan",
    slug: "uk",
  },
  {
    code: "US",
    name: "United States",
    img: "https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format",
    gradient: "from-blue-500/40 via-red-900/45 to-ink",
    unis: "MIT · Arizona State · Illinois",
    intake: "Aug / Jan",
    slug: "usa",
  },
  {
    code: "CA",
    name: "Canada",
    img: "https://images.unsplash.com/photo-1543962226-818f4301073f?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format",
    gradient: "from-red-500/35 via-amber-900/45 to-ink",
    unis: "Toronto · UBC · Waterloo",
    intake: "Sep / Jan",
    slug: "canada",
  },
  {
    code: "AU",
    name: "Australia",
    img: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format",
    gradient: "from-amber-400/40 via-emerald-900/45 to-ink",
    unis: "Melbourne · Sydney · Monash",
    intake: "Feb / Jul",
    slug: "australia",
  },
  {
    code: "DE",
    name: "Germany",
    img: "https://images.unsplash.com/photo-1554072675-66db59dba46f?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format",
    gradient: "from-yellow-400/35 via-neutral-900/50 to-ink",
    unis: "TUM · RWTH Aachen · Heidelberg",
    intake: "Oct / Apr",
    slug: "germany",
  },
  {
    code: "NZ",
    name: "New Zealand",
    img: "https://images.unsplash.com/photo-1714142744624-4667e4e284ec?crop=entropy&cs=srgb&fm=jpg&q=80&w=900&auto=format",
    gradient: "from-emerald-500/40 via-teal-900/50 to-ink",
    unis: "Auckland · Otago · Victoria",
    intake: "Feb / Jul",
    slug: "new-zealand",
  },
];

export default function Destinations() {
  return (
    <section id="destinations" data-testid="destinations-section" className="container-x py-20 lg:py-32">
      <SectionHeading
        overline="Where to next"
        title={
          <>
            Six destinations. <span className="italic text-mint">One flight of stairs.</span>
          </>
        }
        desc="Every country has its own rules, intakes and hidden costs. We keep them all on speed dial so you don't have to."
      />

      <div data-testid="destinations-grid" className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
        {COUNTRIES.map((c, i) => (
          <motion.article
            key={c.slug}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.1 }}
            data-testid={`country-card-${c.slug}`}
            className="group relative h-[400px] overflow-hidden rounded-[28px] border border-white/10 transition-all duration-500 hover:-translate-y-1.5 hover:border-mint/40 hover:shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
          >
            <img
              src={c.img}
              alt={`${c.name} skyline`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.06]"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${c.gradient}`} />
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink via-ink/60 to-transparent" />

            <span className="glass-card absolute left-5 top-5 rounded-full px-3 py-1 font-mono text-[11px] tracking-[0.2em] text-frost">
              {c.code}
            </span>

            <div className="absolute inset-x-6 bottom-6">
              <h3 className="font-display text-3xl font-normal tracking-tight text-white">
                {c.name}
              </h3>
              <p className="mt-2.5 flex items-center gap-2 text-[13px] text-white/75">
                <University className="h-3.5 w-3.5 text-mint" strokeWidth={2} />
                {c.unis}
              </p>
              <div className="mt-5 flex items-center justify-between">
                <span className="flex items-center gap-2 rounded-full border border-white/20 bg-ink/40 px-3.5 py-1.5 text-xs text-white/85 backdrop-blur-sm">
                  <CalendarDays className="h-3.5 w-3.5 text-mint" />
                  Intakes {c.intake}
                </span>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-mint text-ink transition-transform duration-300 group-hover:scale-110" aria-hidden="true">
                  <ArrowUpRight className="h-5 w-5" />
                </span>
              </div>
            </div>
            <Link to={`/destinations/${c.slug}`} data-testid={`country-guide-link-${c.slug}`} aria-label={`Explore the ${c.name} study guide`} className="absolute inset-0 z-10 rounded-[28px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-mint" />
          </motion.article>
        ))}
      </div>
    </section>
  );
}
